/**
 * ProtectedRoute.tsx — client-side route guards. These are convenience UX
 * only (redirect to /login, hide manager-only screens from editors); the
 * real access control is enforced server-side in the Worker (every
 * manager-only route 403s an editor regardless of what the UI shows).
 */
import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import type { Capability } from "@demandhub-cms/shared";
import { useAuth } from "../context/AuthContext";
import { FullScreenSpinner } from "./FullScreenSpinner";
import { EmptyState } from "./ui/empty-state";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * RequireCapability — item 7's granular-RBAC route guard. Unlike
 * RequireManager (a hardcoded, unconditional "manager only" gate for the
 * handful of especially sensitive screens), this checks the signed-in
 * user's actual effective capabilities (`useAuth().can`, sourced from
 * GET /role-permissions/mine) — so a `publisher`, or an `editor` a
 * manager has explicitly granted a capability to, sees and can use the
 * matching screen, while one without it gets the same friendly
 * restricted-screen message a non-manager gets from RequireManager. A
 * manager always passes, same as every capability check server-side.
 */
export function RequireCapability({ capability, children }: { capability: Capability; children: ReactNode }) {
  const { user, loading, can } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(capability)) {
    return (
      <div className="p-8">
        <EmptyState
          icon={ShieldAlert}
          title="Restricted"
          description="Your role doesn't have this permission. The API enforces this too — you'd get a 403 even calling it directly. A manager can grant it from Settings -> Roles & Permissions."
        />
      </div>
    );
  }
  return <>{children}</>;
}

export function RequireManager({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "manager") {
    return (
      <div className="p-8">
        <EmptyState
          icon={ShieldAlert}
          title="Restricted to managers"
          description="This screen is manager-only. The API enforces this too — you'd get a 403 even calling it directly."
        />
      </div>
    );
  }
  return <>{children}</>;
}
